import { useState } from 'react';

import { Button } from '../../shared/ui/button';
import { Input } from '../../shared/ui/input';
import { PanelEmpty, SectionPanel } from '../../shared/ui/section-panel';
import { Table, type IColumn } from '../../shared/ui/table';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { ApiError } from '../../shared/api/errors';
import { cn } from '../../shared/lib/cn';
import { relativeTime, shortDate } from '../../shared/lib/format';
import {
  domainPhase,
  PHASE_DOT,
  renewalLate,
  type TCustomDomain,
} from '../../entities/custom-domain';
import { DomainDetail } from './domain-detail';
import { RemoveDomainDialog } from './remove-domain-dialog';
import { useCustomDomains } from './use-custom-domains';

const perDay = (count: number): string =>
  `${count.toLocaleString('en-US')} request${count === 1 ? '' : 's'} in the past day`;

const summary = (domain: TCustomDomain, now: number, timeZone?: string): string => {
  const checked = domain.dnsCheckedAt
    ? `checked ${relativeTime(domain.dnsCheckedAt, now, timeZone)}`
    : 'not checked yet';
  switch (domainPhase(domain)) {
    case 'live': {
      const expiry = domain.tlsExpiresAt ? shortDate(domain.tlsExpiresAt, timeZone) : null;
      if (expiry && renewalLate(domain, now)) {
        return `Live · the certificate could not be renewed · still works until ${expiry}`;
      }
      return `Live · ${perDay(domain.requestsDay)}${expiry ? ` · certificate valid until ${expiry}` : ''}`;
    }
    case 'certificate':
      return `Record found · issuing a certificate · ${checked}`;
    case 'answering':
      return `Certificate ready · starting to answer · ${checked}`;
    case 'lost':
      return `The DNS record is gone · ${checked}`;
    default:
      return `Waiting for your DNS record · ${checked}`;
  }
};

export const CustomDomainsPanel = ({ timeZone }: { timeZone?: string }) => {
  const { domains, add, check, remove } = useCustomDomains();
  const [hostname, setHostname] = useState('');
  const [removing, setRemoving] = useState<TCustomDomain | null>(null);
  const now = Date.now();
  const list = domains.data;
  const rows = list?.data ?? [];
  const addError = add.error instanceof ApiError ? add.error.message : null;

  if (domains.isError) {
    return <ErrorState error={domains.error} onRetry={() => void domains.refetch()} />;
  }
  if (!list) return <LoadingState rows={3} />;

  const columns: IColumn<TCustomDomain>[] = [
    {
      key: 'hostname',
      header: 'Hostname',
      width: 'grow',
      render: (domain) => {
        const phase = domainPhase(domain);
        const late = phase === 'live' && renewalLate(domain, now);
        const line = summary(domain, now, timeZone);
        return (
          <span className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden
              className={cn(
                'size-dot shrink-0 rounded-full',
                late ? 'bg-danger' : PHASE_DOT[phase],
              )}
            />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-primary">{domain.hostname}</span>
              <span
                title={line}
                className={cn('truncate font-sans text-meta', late ? 'text-danger' : 'text-muted')}
              >
                {line}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: 'w-col-row-actions',
      ui: true,
      render: (domain) => (
        <span className="flex items-center justify-end gap-1">
          {domainPhase(domain) !== 'live' && (
            <Button
              variant="ghost"
              size="sm"
              waiting={check.isPending && check.variables === domain.id}
              waitingLabel="Checking"
              onClick={() => check.mutate(domain.id)}
            >
              Check now
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setRemoving(domain)}>
            Remove
          </Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <SectionPanel
        title="Your hostnames"
        note={list.enabled ? `${rows.length} added` : 'not available on this installation'}
      >
        {list.enabled ? (
          <form
            className="flex items-start gap-2 border-b border-border-sunken px-card py-4"
            onSubmit={(event) => {
              event.preventDefault();
              add.mutate(hostname.trim(), {
                onSuccess: () => {
                  setHostname('');
                  add.reset();
                },
              });
            }}
          >
            <div className="flex-1">
              <Input
                aria-label="Hostname"
                mono
                placeholder="hooks.yourcompany.com"
                value={hostname}
                invalid={addError !== null}
                onChange={(event) => {
                  setHostname(event.target.value);
                  add.reset();
                }}
              />
              {addError && <p className="mt-label text-meta text-danger">{addError}</p>}
            </div>
            <Button
              type="submit"
              disabled={hostname.trim().length === 0}
              waiting={add.isPending}
              waitingLabel="Adding"
            >
              Add hostname
            </Button>
          </form>
        ) : (
          <PanelEmpty title="Your own hostnames aren’t turned on here">
            Whoever runs hookpipe for your company can turn them on. Until then, senders use our
            address below — it works the same way.
          </PanelEmpty>
        )}

        {list.enabled && rows.length === 0 && (
          <PanelEmpty title="No hostnames yet">
            Type one you own above, like hooks.yourcompany.com. We then show the DNS record to add
            at your DNS provider, and it goes live on its own once the record is found.
          </PanelEmpty>
        )}

        {rows.length > 0 && (
          <Table
            tall
            layoutId="settings-domains"
            columns={columns}
            rows={rows}
            rowKey={(domain) => domain.id}
            expanded={(domain) =>
              domainPhase(domain) !== 'live' && (
                <div className="border-b border-border-row bg-field-muted px-card py-4 pl-10">
                  <DomainDetail domain={domain} list={list} />
                </div>
              )
            }
          />
        )}
      </SectionPanel>

      <SectionPanel title="Our address" note="always on">
        <div className="flex items-center gap-3 px-card py-4">
          <span aria-hidden className="size-dot shrink-0 rounded-full bg-success" />
          <span className="min-w-0 flex-1">
            <span className="block font-mono text-data text-primary">{list.centralHost}</span>
            <span className="block text-meta text-muted">
              Every source answers here too, whatever hostnames you add. Adding one never changes an
              address that already works.
            </span>
          </span>
        </div>
      </SectionPanel>

      <RemoveDomainDialog
        domain={removing}
        waiting={remove.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={(domain) => remove.mutate(domain.id, { onSuccess: () => setRemoving(null) })}
      />
    </>
  );
};
